import { useEffect, useState } from 'react'

/**
 * Autosave a Formik form when the user leaves a field (blur),
 * if the form is dirty and valid.
 *
 * Pass `{ force: true }` after setFieldValue on selects/checkboxes — the
 * render-prop `dirty` flag is still false until the next React render, so
 * blur-style autosave would otherwise no-op and skip the update API.
 */
export const autosaveOnBlur = async (formikBag, options = {}) => {
  if (!formikBag) {
    return
  }
  const { dirty, isSubmitting, submitForm, validateForm } = formikBag
  const force = !!(options && options.force)
  if (isSubmitting) {
    return
  }
  if (!force && !dirty) {
    return
  }
  const errors = await validateForm()
  if (errors && Object.keys(errors).length > 0) {
    return
  }
  await submitForm()
}

/**
 * Call from input onBlur. Defers so relatedTarget / Formik state settle.
 * Returns a promise so callers can lock the UI until autosave finishes.
 */
export const queueAutosaveOnBlur = (formikBag, options = {}) => {
  return new Promise((resolve) => {
    window.setTimeout(() => {
      resolve(autosaveOnBlur(formikBag, options))
    }, 0)
  })
}

/**
 * After a select/checkbox change: await setFieldValue, then submit.
 * Uses force so we do not depend on stale render-prop `dirty`.
 * submitForm/validateForm read Formik's latest internal state.
 */
export const autosaveAfterFieldChange = async (formikBag, setValueResult) => {
  if (setValueResult != null && typeof setValueResult.then === 'function') {
    await setValueResult
  }
  await autosaveOnBlur(formikBag, { force: true })
}

/** Submit the open inline editor form so blur-close persists dirty fields. */
export const flushInlineEditorForm = () => {
  const form = document.querySelector('.experience-add-form form')
  if (form) {
    form.requestSubmit()
  }
}

/** Flush then run close after Formik submit settles. */
export const closeInlineEditorAfterFlush = (closeFn) => {
  flushInlineEditorForm()
  window.setTimeout(closeFn, 50)
}

/**
 * Blur the focused field in the Build editor and wait for autosave to finish
 * so "Next" can save + navigate in one click.
 */
export const flushActiveBuildForm = async ({
  rootSelector = '.experience-editor-main',
  timeoutMs = 5000,
} = {}) => {
  if (typeof document === 'undefined') {
    return true
  }
  const root = document.querySelector(rootSelector)
  if (!root) {
    return true
  }

  const active = document.activeElement
  if (active && root.contains(active) && typeof active.blur === 'function') {
    active.blur()
  }

  // Let queueAutosaveOnBlur's setTimeout(0) run.
  await new Promise((resolve) => window.setTimeout(resolve, 0))

  const form = root.querySelector('form')
  if (form && typeof form.requestSubmit === 'function') {
    try {
      form.requestSubmit()
    } catch (e) {
      // Ignore — some forms may not be submittable.
    }
  }

  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    const busy = root.querySelector('[aria-busy="true"]')
    if (!busy) {
      break
    }
    await new Promise((resolve) => window.setTimeout(resolve, 40))
  }

  // One more tick for any follow-up queue save to start/settle.
  await new Promise((resolve) => window.setTimeout(resolve, 0))
  const settleDeadline = Date.now() + 1500
  while (Date.now() < settleDeadline) {
    const busy = root.querySelector('[aria-busy="true"]')
    if (!busy) {
      break
    }
    await new Promise((resolve) => window.setTimeout(resolve, 40))
  }

  // Formik validation errors render on the next paint; report them so the
  // caller can stay on this step.
  await new Promise((resolve) => window.setTimeout(resolve, 50))
  const invalid = root.querySelector('.is-invalid')
  if (invalid) {
    if (typeof invalid.scrollIntoView === 'function') {
      invalid.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    if (typeof invalid.focus === 'function') {
      invalid.focus({ preventScroll: true })
    }
    return false
  }
  return true
}

/**
 * Option 2 — queue save:
 * 1) Save the submitted values
 * 2) Re-apply whatever is in valuesRef (edits during the request)
 * 3) If that snapshot differs, save once more
 *
 * Does not loop forever while the user keeps typing; a later blur/change
 * starts the next cycle. Pair with dirty reporting + beforeunload.
 */
export const runQueuedFormikSave = async ({
  values,
  valuesRef,
  buildPayload,
  save,
  resetForm,
  setInitialValues,
  shouldSkip,
}) => {
  if (typeof shouldSkip === 'function' && shouldSkip()) {
    return
  }
  const payloadOf = (v) =>
    typeof buildPayload === 'function' ? buildPayload(v) : v
  const snap = JSON.stringify(payloadOf(values))
  await save(payloadOf(values))

  let latest = valuesRef?.current ?? values
  if (typeof setInitialValues === 'function') {
    setInitialValues(latest)
  }
  if (typeof resetForm === 'function') {
    resetForm({ values: latest })
  }
  if (typeof shouldSkip === 'function' && shouldSkip()) {
    return
  }
  if (JSON.stringify(payloadOf(latest)) === snap) {
    return
  }
  await save(payloadOf(latest))
  latest = valuesRef?.current ?? latest
  if (typeof setInitialValues === 'function') {
    setInitialValues(latest)
  }
  if (typeof resetForm === 'function') {
    resetForm({ values: latest })
  }
}

// —— Local dirty registry (header + beforeunload) ——

const dirtyForms = new Set()
const dirtyListeners = new Set()

const emitDirty = () => {
  const dirty = dirtyForms.size > 0
  dirtyListeners.forEach((fn) => {
    try {
      fn(dirty)
    } catch (e) {
      // ignore listener errors
    }
  })
}

/** Mark a Build form as having unsaved local edits (Formik dirty). */
export const reportExperienceFormDirty = (formId, isDirty) => {
  if (!formId) return
  if (isDirty) {
    dirtyForms.add(formId)
  } else {
    dirtyForms.delete(formId)
  }
  emitDirty()
}

export const subscribeExperienceFormDirty = (listener) => {
  if (typeof listener !== 'function') {
    return () => {}
  }
  dirtyListeners.add(listener)
  listener(dirtyForms.size > 0)
  return () => {
    dirtyListeners.delete(listener)
  }
}

export const isAnyExperienceFormDirty = () => dirtyForms.size > 0

/** Renders nothing; keeps the dirty registry in sync with Formik.dirty. */
export const FormikDirtyReporter = ({ id, dirty }) => {
  useEffect(() => {
    reportExperienceFormDirty(id, !!dirty)
    return () => reportExperienceFormDirty(id, false)
  }, [id, dirty])
  return null
}

/** Browser “Leave site?” when any experience form still has local edits. */
export const useExperienceUnsavedGuard = () => {
  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (dirtyForms.size === 0) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])
}

/** Subscribe CreateQuest header to local dirty state. */
export const useExperienceFormDirtyFlag = () => {
  const [hasUnsavedLocal, setHasUnsavedLocal] = useState(false)
  useEffect(() => subscribeExperienceFormDirty(setHasUnsavedLocal), [])
  return hasUnsavedLocal
}

let setupNameValue = null
const setupNameListeners = new Set()

/** Live Internal name from the Experience Details form (null = form not mounted). */
export const reportSetupName = (name) => {
  setupNameValue = name
  setupNameListeners.forEach((fn) => fn(name))
}

export const useSetupNameMissing = () => {
  const [name, setName] = useState(setupNameValue)
  useEffect(() => {
    setupNameListeners.add(setName)
    setName(setupNameValue)
    return () => setupNameListeners.delete(setName)
  }, [])
  return name !== null && !String(name).trim()
}

/** Renders nothing; mirrors the Internal name field for the Next button. */
export const SetupNameReporter = ({ name }) => {
  useEffect(() => {
    reportSetupName(name || '')
  }, [name])
  useEffect(() => () => reportSetupName(null), [])
  return null
}
